import { gql, useQuery } from '@apollo/client';

import type {
  GetDealRemittanceSummaries,
  GetDealRemittanceSummariesVariables,
} from './__generated__/GetDealRemittanceSummaries';

/**
 * Fetches aggregated remittance summaries by payment month for a deal (getDealRemittanceSummaries).
 * Used for the historic cashflow chart on the remittance cashflow profile page.
 *
 * Two bases both get selected:
 * - Investor-share (`total_*_cents` unsuffixed for p/i/payments, `*_participation_cents`
 *   for balances + prep/def/loss): drives any buyer-specific view (e.g. per-month
 *   wire reconciliation).
 * - Full tape (`*_full_cents` for p/i/payments, unsuffixed for balances + prep/def/loss,
 *   `total_fees_cents` for servicing): drives the pool-level chart and MonthlyTable
 *   introduced in Option A (ENG-2659). Chart shows the traded pool's cashflows so
 *   both buyer and seller see the same numbers; per-party views are reconstructed
 *   separately.
 */
export const GET_DEAL_REMITTANCE_SUMMARIES = gql`
  query GetDealRemittanceSummaries($input: GetDealRemittanceSummariesInput!) {
    getDealRemittanceSummaries(input: $input) {
      summaries_by_payment_month {
        payment_month
        summary {
          # Investor-share aggregates (buyer-view reconciliation).
          total_principal_cents
          total_interest_cents
          total_payments_cents
          total_prepayments_participation_cents
          total_defaults_participation_cents
          total_losses_participation_cents
          starting_balance_participation_cents
          ending_balance_participation_cents
          # Full-tape aggregates (chart / MonthlyTable pool-level view, sidecar split).
          total_principal_full_cents
          total_interest_full_cents
          total_payments_full_cents
          total_prepayments_cents
          total_defaults_cents
          total_losses_cents
          starting_balance_cents
          ending_balance_cents
          total_fees_cents
          loan_count
          delinquent_count
          prepayment_count
        }
      }
    }
  }
`;

export function useGetDealRemittanceSummaries(dealId: string | undefined) {
  const skip = !dealId?.trim();
  const { data, loading, error } = useQuery<
    GetDealRemittanceSummaries,
    GetDealRemittanceSummariesVariables
  >(GET_DEAL_REMITTANCE_SUMMARIES, {
    variables: { input: { deal_id: dealId ?? '' } },
    skip,
    fetchPolicy: 'network-only',
  });

  const summariesByPaymentMonth =
    data?.getDealRemittanceSummaries?.summaries_by_payment_month ?? [];

  return { data: { summaries_by_payment_month: summariesByPaymentMonth }, loading, error };
}
